import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import * as actions from './actions';
import { userRoles, categories } from 'util/appConstants';
import { useActions } from 'hooks/useActions';
import { getCookie } from 'functions/getCookie';
import { getConfig } from 'functions/getConfig';
import Path from 'routes/Path';
import { Wrapper, Container, Button, LinkButton } from '../../components';
import Filter from './filter';
import SearchBar from '../../components/SearchBar';
import Sorting from '../../components/Sorting';
import CustomTestCard from '../../components/CustomTestCard/customTestCard';
import Pagination from '../../components/Pagination';
import { Spinner } from 'components/Spinner/spinner';
import { useDetectOutsideClick } from 'hooks/useDetectOutsideClick';
import Tour from 'components/Tour/tour';
import { TOUR_DATA } from './customTestsTourData';
import { HeadingButtonContainer, DropDownListContainer, DropDownList, ListItem } from './styles';
import {
  Header,
  HeadingContainer,
  HeaderContainer,
  ContentContainer,
  LeftContainer,
  RightContainer,
  RightHeaderContainer,
  SortByLabel,
  ChallengeGrid,
} from '../ChallengeListPage/styles';

let defaultFilterOptions = {
  selectAll: true,
  adaptiveTest: true,
  generalSkills: true,
  multichoice: true,
  code: true,
  challenge: true,
  exercise: true,
  freecode: true,
  publicFreecode: false,
  published: true,
  custom: false,
  archived: false,
};
let isCopiedTestMultichoice = false;
let isNewTestMultichoice = false;

export default function CustomTestsPage() {
  const [tourCompleted, setTourCompleted] = useState(getCookie('fsTourCustomTestsDone'));
  const [testsList, setTestsList] = useState([]);
  const [paginatedItems, setPaginatedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState({
    value: 'createdAt',
    descendingOrder: true,
  });
  const [filterOptions, setFilterOptions] = useState(defaultFilterOptions);
  const dropdownRef = useRef(null);
  const [isOpenDropdown, setIsOpenDropdown] = useDetectOutsideClick(dropdownRef, false);
  const itemsPerPage = 12;

  const { getTests, publishAndArchiveTest, copyTest, createTest } = useActions(actions);

  let { error, loading, tests, publishedOrArchivedTest, copiedTestId, newTest } = useSelector(
    (state) => state.customTests
  );
  let { userRole, userId, user, company } = useSelector((state) => state.auth);

  if (userRole === userRoles.CANDIDATE) defaultFilterOptions.custom = true;

  const dropDownOptions = [
    categories.CHALLENGE.simpleName,
    categories.EXERCISE.simpleName,
    categories.FREECODE.simpleName,
  ];
  if (userRole !== userRoles.CANDIDATE)
    dropDownOptions.push(
      categories.CODE.simpleName,
      categories.MULTICHOICE.simpleName,
      categories.GENERAL_SKILLS.simpleName
    );

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    if (userRole) {
      console.log('getTests...');
      getTests();
    }
  }, [userRole]);

  useEffect(() => {
    let filteredTests = tests.filter(searchQueryFilter(searchQuery)).filter(filterTests);
    setTestsList(filteredTests);
    if (filteredTests.length <= itemsPerPage) setPaginatedItems(filteredTests);
  }, [tests]);

  useEffect(() => {
    if (publishedOrArchivedTest._id) {
      if (publishedOrArchivedTest.published) toast.success('Test published successfully');
      if (publishedOrArchivedTest.archived) toast.success('Test archived successfully');
    }
  }, [publishedOrArchivedTest]);

  useEffect(() => {
    if (copiedTestId) {
      if (isCopiedTestMultichoice) window.location = `${getConfig().REDIRECTION_ROOT_URL}/edit-test/${copiedTestId}/`;
      else window.location = `${getConfig().REDIRECTION_ROOT_URL}/freecode-test-editor/${copiedTestId}`;
    }
  }, [copiedTestId]);

  useEffect(() => {
    if (newTest && newTest._id) {
      if (isNewTestMultichoice) window.location = `${getConfig().REDIRECTION_ROOT_URL}/edit-test/${newTest._id}/`;
      else window.location = `${getConfig().REDIRECTION_ROOT_URL}/freecode-test-editor/${newTest._id}`;
    }
  }, [newTest]);

  useEffect(() => {
    let filteredTests = tests.filter(searchQueryFilter(searchQuery)).filter(filterTests);
    setTestsList(filteredTests);
    if (filteredTests.length <= itemsPerPage) setPaginatedItems(filteredTests.sort(sortFunc));
  }, [searchQuery, sortBy, filterOptions]);

  const searchQueryFilter = (searchQuery) => (object) => {
    return String(object.name).toLowerCase().indexOf(searchQuery.toLowerCase()) > -1;
  };

  const filterTests = (test) => {
    if (
      userRole === userRoles.CANDIDATE &&
      (test.category === categories.MULTICHOICE.category ||
        test.category === categories.GENERAL_SKILLS.category ||
        test.category === categories.CODE.category)
    )
      return false;
    let isPassedFirstCheck = false;
    let testOwnerCheck =
      userRole === userRoles.SUPER_ADMIN ||
      (userRole !== userRoles.CANDIDATE && company._id === test.company) ||
      (userRole === userRoles.CANDIDATE && test.account === userId) ||
      (filterOptions.publicFreecode && test.publicToCommunity);

    if (test.adaptiveTest && filterOptions.adaptiveTest) isPassedFirstCheck = true;
    if (test.category === categories.MULTICHOICE.category && filterOptions.multichoice) isPassedFirstCheck = true;
    if (test.category === categories.GENERAL_SKILLS.category && filterOptions.generalSkills) isPassedFirstCheck = true;
    if (test.category === categories.CODE.category && filterOptions.code) isPassedFirstCheck = true;
    if (test.category === categories.CHALLENGE.category && filterOptions.challenge && testOwnerCheck)
      isPassedFirstCheck = true;
    if (test.category === categories.EXERCISE.category && filterOptions.exercise && testOwnerCheck)
      isPassedFirstCheck = true;
    if (test.category === categories.FREECODE.category && test.isFreecode && filterOptions.freecode && testOwnerCheck)
      isPassedFirstCheck = true;

    if (isPassedFirstCheck) {
      if (test.custom && filterOptions.custom) return true;
      if (test.published && filterOptions.published) return true;
      if (test.archived && filterOptions.archived) return true;
    }
    return false;
  };

  const sortFunc = useCallback(
    (a, b) => {
      let desc = 1;
      let asc = -1;
      if (typeof a[sortBy.value] === 'number') {
        return sortBy.descendingOrder ? b[sortBy.value] - a[sortBy.value] : a[sortBy.value] - b[sortBy.value];
      }
      if (String(a[sortBy.value]).toLowerCase() > String(b[sortBy.value]).toLowerCase()) {
        return sortBy.descendingOrder
          ? sortBy.value === 'createdAt'
            ? asc
            : desc
          : sortBy.value === 'createdAt'
          ? desc
          : asc;
      }
      if (String(a[sortBy.value]).toLowerCase() < String(b[sortBy.value]).toLowerCase()) {
        return sortBy.descendingOrder
          ? sortBy.value === 'createdAt'
            ? desc
            : asc
          : sortBy.value === 'createdAt'
          ? asc
          : desc;
      }
      return 0;
    },
    [sortBy]
  );

  const toggleTour = () => {
    if (!tourCompleted) {
      document.cookie = `fsTourCustomTestsDone=true; Domain=${
        getConfig().COOKIE
      }; Max-Age=31536000; Path=/; SameSite=Lax`;
    }
    setTourCompleted(!tourCompleted);
  };

  const onOptionClicked = (value) => () => {
    setIsOpenDropdown(false);
    let operation = {};
    isNewTestMultichoice =
      value === categories.MULTICHOICE.simpleName || value === categories.GENERAL_SKILLS.simpleName ? true : false;
    if (value === categories.MULTICHOICE.simpleName) operation = { category: categories.MULTICHOICE.category };
    if (value === categories.GENERAL_SKILLS.simpleName) operation = { category: categories.GENERAL_SKILLS.category };
    if (value === categories.CODE.simpleName) operation = { category: categories.CODE.category };
    if (value === categories.CHALLENGE.simpleName) operation = { category: categories.CHALLENGE.category };
    if (value === categories.EXERCISE.simpleName) operation = { category: categories.EXERCISE.category };
    if (value === categories.FREECODE.simpleName)
      operation = { category: categories.FREECODE.category, isFreecode: true };
    createTest(operation, isNewTestMultichoice);
  };

  return (
    <Wrapper padding="60px 0 0 0" minHeight="100%">
      <Container column>
        <HeadingContainer>
          <Header id="customTestsPageHeader">
            Custom {userRole && (userRole === userRoles.CANDIDATE ? 'Freecode' : 'Tests')}
          </Header>
          <HeadingButtonContainer>
            <Button
              primaryGreenBorder
              padding="5px 15px"
              margin="0 0 0 10px"
              fontSize="14px"
              primaryBlue
              id="toggleTourButton"
              title="Toggle a tour explaining the page"
              onClick={toggleTour}
            >
              <i className="fa fa-question" style={{ marginRight: '5px' }}></i>Tour
            </Button>
            <Button
              primaryGreenBorder
              padding="5px 15px"
              margin="0 0 0 10px"
              fontSize="14px"
              primaryBlue
              id="createTestButton"
              title="Create something new"
              onClick={() => setIsOpenDropdown(!isOpenDropdown)}
            >
              <i className="fa fa-plus" style={{ marginRight: '5px' }}></i>Create{' '}
              {userRole && (userRole === userRoles.CANDIDATE ? 'Freecode' : 'Test')}
              {isOpenDropdown && (
                <DropDownListContainer>
                  <DropDownList ref={dropdownRef}>
                    {dropDownOptions.map((option) => (
                      <ListItem onClick={onOptionClicked(option)} key={option}>
                        {option}
                      </ListItem>
                    ))}
                  </DropDownList>
                </DropDownListContainer>
              )}
            </Button>

            {userRole && userRole !== userRoles.CANDIDATE && (
              <LinkButton
                primaryGreenBorder
                removeMargin
                padding="5px 15px"
                margin="0 0 0 10px"
                fontSize="14px"
                primaryBlue
                href={Path.questionFeedbackPage}
              >
                <i className="fa fa-comments-o" style={{ marginRight: '5px' }}></i>Feedbacks
              </LinkButton>
            )}
          </HeadingButtonContainer>
        </HeadingContainer>
        <ContentContainer>
          <LeftContainer>
            <Filter
              filterOptions={filterOptions}
              defaultFilterOptions={defaultFilterOptions}
              userRole={userRole}
              clearFilters={() => setFilterOptions(defaultFilterOptions)}
              filterValues={(values) => setFilterOptions(values)}
            />
          </LeftContainer>
          <RightContainer>
            <HeaderContainer>
              <SearchBar value={searchQuery} onChange={setSearchQuery} />
              <RightHeaderContainer>
                <SortByLabel>Sort by:</SortByLabel>
                <Sorting
                  value={sortBy.value}
                  descendingOrder={sortBy.descendingOrder}
                  options={[
                    { value: 'name', label: 'Name' },
                    { value: 'categoryName', label: 'Category' },
                    { value: 'createdAt', label: 'Newest' },
                    { value: 'editedAt', label: 'Edited' },
                    { value: 'owner', label: 'Owner' },
                  ]}
                  onChange={(value) => setSortBy(value)}
                />
              </RightHeaderContainer>
            </HeaderContainer>
            {loading && <Spinner />}
            <ChallengeGrid>
              {paginatedItems.length >= 0 &&
                paginatedItems.map((test) => (
                  <CustomTestCard
                    test={test}
                    key={test._id}
                    user={user}
                    publishOrArchiveTest={(values) =>
                      publishAndArchiveTest(values.test, { operation: values.operation })
                    }
                    copyTest={(values) => {
                      isCopiedTestMultichoice = values.isMultichoice;
                      copyTest(values._id, values.isMultichoice);
                    }}
                  />
                ))}
            </ChallengeGrid>
            {testsList.length > itemsPerPage && (
              <Pagination items={testsList.sort(sortFunc)} itemsPerPage={itemsPerPage} getItems={setPaginatedItems} />
            )}
          </RightContainer>
        </ContentContainer>
      </Container>
      {!tourCompleted && <Tour tourData={TOUR_DATA} completed={tourCompleted} setCompleted={toggleTour} />}
    </Wrapper>
  );
}
